/**
 * Calendar with Theme Filters - Client Component
 * Wraps CalendarView with theme filtering capabilities
 * Updates URL query params when filters change
 */

'use client';

import { FilterThemes } from '@/components/filters/filter-themes';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import type { EventTeaser } from '../types';
import { CalendarView } from './calendar/calendar-view';

interface CalendarWithFiltersProps {
  /** Initial events loaded from server */
  initialEvents: EventTeaser[];
  /** Base URL for event links */
  baseUrl?: string;
}

/**
 * Extract unique themes from events
 */
function extractThemes(events: EventTeaser[]): Array<{ id: string; name: string }> {
  const themeMap = new Map<string, { id: string; name: string }>();

  events.forEach((event) => {
    event.theme?.forEach((theme) => {
      if (theme?.id && theme?.name && !themeMap.has(theme.id)) {
        themeMap.set(theme.id, {
          id: theme.id,
          name: theme.name,
        });
      }
    });
  });

  return Array.from(themeMap.values()).sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Filter events by theme IDs
 */
function filterEventsByThemes(
  events: EventTeaser[],
  themeIds: string[],
  matchMode: 'OR' | 'AND'
): EventTeaser[] {
  if (themeIds.length === 0) {
    return events;
  }

  return events.filter((event) => {
    const eventThemeIds = event.theme?.map((t) => t?.id).filter(Boolean) ?? [];

    if (matchMode === 'AND') {
      // All themes must be present
      return themeIds.every((themeId) => eventThemeIds.includes(themeId));
    } else {
      // At least one theme must match
      return themeIds.some((themeId) => eventThemeIds.includes(themeId));
    }
  });
}

// Default theme IDs to filter by
const DEFAULT_THEME_IDS = ['4555', '4590'];

export function CalendarWithFilters({
  initialEvents,
  baseUrl = '/events',
}: CalendarWithFiltersProps) {
  const router = useRouter();
  const searchParams = useSearchParams();

  // Get theme IDs from URL params, or use defaults
  const themeIdsFromUrl = useMemo(() => {
    const themeIdsParam = searchParams.get('themeIds');
    if (!themeIdsParam) return DEFAULT_THEME_IDS;
    return themeIdsParam
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
  }, [searchParams]);

  // Get match mode from URL params (default: 'OR')
  const matchModeFromUrl = useMemo(() => {
    const mode = searchParams.get('matchMode');
    return mode === 'AND' ? ('AND' as const) : ('OR' as const);
  }, [searchParams]);

  // State for selected themes and match mode
  const [selectedThemes, setSelectedThemes] = useState<string[]>(themeIdsFromUrl);
  const [themeFilterMode, setThemeFilterMode] = useState<'OR' | 'AND'>(matchModeFromUrl);

  // Initialize with default theme IDs if no URL params are present (only once on mount)
  useEffect(() => {
    const themeIdsParam = searchParams.get('themeIds');
    if (!themeIdsParam) {
      // No URL params, use defaults and update URL
      setSelectedThemes(DEFAULT_THEME_IDS);
      // Update URL with default theme IDs
      const params = new URLSearchParams();
      params.set('themeIds', DEFAULT_THEME_IDS.join(','));
      params.set('matchMode', 'OR');
      router.replace(`/events/calendar?${params.toString()}`, { scroll: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Sync state with URL params when they change externally
  useEffect(() => {
    const themeIdsParam = searchParams.get('themeIds');
    if (themeIdsParam) {
      // URL params exist, sync state
      setSelectedThemes(themeIdsFromUrl);
      setThemeFilterMode(matchModeFromUrl);
    }
  }, [themeIdsFromUrl, matchModeFromUrl, searchParams]);

  // Extract available themes from events
  const themes = useMemo(() => extractThemes(initialEvents), [initialEvents]);

  // Filter events based on selected themes
  const filteredEvents = useMemo(() => {
    return filterEventsByThemes(initialEvents, selectedThemes, themeFilterMode);
  }, [initialEvents, selectedThemes, themeFilterMode]);

  // Update URL when filters change
  const updateUrl = (newThemeIds: string[], newMatchMode: 'OR' | 'AND') => {
    const params = new URLSearchParams(searchParams.toString());

    if (newThemeIds.length > 0) {
      params.set('themeIds', newThemeIds.join(','));
      params.set('matchMode', newMatchMode);
    } else {
      params.delete('themeIds');
      params.delete('matchMode');
    }

    // Update URL without page reload
    router.push(`/events/calendar?${params.toString()}`, { scroll: false });
  };

  // Handle theme toggle
  const handleToggleTheme = (themeId: string) => {
    const newSelectedThemes = selectedThemes.includes(themeId)
      ? selectedThemes.filter((id) => id !== themeId)
      : [...selectedThemes, themeId];

    setSelectedThemes(newSelectedThemes);
    updateUrl(newSelectedThemes, themeFilterMode);
  };

  // Handle filter mode change
  const handleFilterModeChange = (mode: 'OR' | 'AND') => {
    setThemeFilterMode(mode);
    updateUrl(selectedThemes, mode);
  };

  // Handle clear selection
  const handleClearSelection = () => {
    setSelectedThemes([]);
    updateUrl([], themeFilterMode);
  };

  return (
    <div className="space-y-4">
      {/* Theme Filter */}
      {themes.length > 0 && (
        <div className="rounded-lg border bg-card p-4 hidden">
          <FilterThemes
            themes={themes}
            selectedThemes={selectedThemes}
            filterMode={themeFilterMode}
            onToggleTheme={handleToggleTheme}
            onFilterModeChange={handleFilterModeChange}
            onClearSelection={handleClearSelection}
          />
        </div>
      )}

      {/* Calendar View */}
      <CalendarView events={filteredEvents} initialView="month" baseUrl={baseUrl} />
    </div>
  );
}

